import { useState } from "react";
import { CircleAlertIcon, FileClockIcon, RefreshCwIcon, XIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/ui/empty-state";
import { Frame, FrameAction, FrameDescription, FrameFooter, FrameHeader, FramePanel, FrameTitle } from "@/components/ui/frame";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { RelativeTime } from "@/components/dashboard_sourcing/RelativeTime";
import { changedBy, diffPlan, type PlanDiffLine } from "./format";
import { usePlanChanges } from "./useActivity";
import type { OpsPlanChange, StoredPlanSummary } from "./types";

/** Lines shown per change before "Show all": a created plan lists every field. */
const DIFF_PREVIEW = 8;

/** The Plan changes tab: every stored plan's current version, then the change history (one client's, once picked). */
export function PlansTab({ slug, onPick }: { slug: string | null; onPick: (slug: string | null) => void }) {
  const feed = usePlanChanges(true, slug);
  const names = new Map(feed.plans.map((p) => [p.slug, p.name] as const));
  const picked = slug ? (names.get(slug) ?? slug) : null;

  return (
    <div className="flex flex-col gap-6">
      {feed.error && (
        <Alert variant="destructive">
          <AlertTitle>
            <CircleAlertIcon />
            Could not load the plan history
          </AlertTitle>
          <AlertDescription>{feed.error}</AlertDescription>
        </Alert>
      )}

      {feed.plans.length > 0 ? (
        <Plans plans={feed.plans} active={slug} onPick={(s) => onPick(s === slug ? null : s)} />
      ) : (
        feed.loading && <Skeleton className="h-40 rounded-xl" />
      )}

      <Frame>
        <FrameHeader>
          <FrameTitle>{picked ? `${picked} changes` : "Changes"}</FrameTitle>
          <FrameDescription>Who changed what and why, newest first (changes are made with update_client_plan)</FrameDescription>
          <FrameAction>
            {slug && (
              <Button variant="ghost" size="sm" onClick={() => onPick(null)}>
                <XIcon />
                Every client
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => void feed.refresh()} disabled={feed.loading}>
              {feed.loading ? <Spinner /> : <RefreshCwIcon />}
              Refresh
            </Button>
          </FrameAction>
        </FrameHeader>
        <FramePanel className="p-0">
          {feed.changes.length === 0 ? (
            <div className="p-4">{feed.loading ? <Skeleton className="h-24" /> : <EmptyState icon={<FileClockIcon />} text="No plan changes yet" />}</div>
          ) : (
            <ul className="divide-y">
              {feed.changes.map((change) => (
                <ChangeRow key={change._id} change={change} name={names.get(change.slug) ?? change.slug} />
              ))}
            </ul>
          )}
        </FramePanel>
        {feed.next && (
          <FrameFooter className="flex justify-center">
            <Button variant="secondary" size="sm" onClick={() => void feed.loadMore()} disabled={feed.loadingMore}>
              {feed.loadingMore && <Spinner />}
              Load older changes
            </Button>
          </FrameFooter>
        )}
      </Frame>
    </div>
  );
}

function Plans({ plans, active, onPick }: { plans: StoredPlanSummary[]; active: string | null; onPick: (slug: string) => void }) {
  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Client plans</FrameTitle>
        <FrameDescription>The numbers each managed client is run by · pick one to see its history</FrameDescription>
      </FrameHeader>
      <Table variant="card">
        <TableHeader>
          <TableRow>
            <TableHead>Client</TableHead>
            <TableHead className="text-right">Version</TableHead>
            <TableHead>Last change</TableHead>
            <TableHead>By</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {plans.map((p) => (
            <TableRow
              key={p.slug}
              onClick={() => onPick(p.slug)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onPick(p.slug);
                }
              }}
              tabIndex={0}
              className={cn("cursor-pointer outline-none focus-visible:bg-muted/50", active === p.slug && "bg-muted/60")}
              aria-selected={active === p.slug}
            >
              <TableCell>
                <span className="font-medium">{p.name}</span>
                <span className="ml-2 font-mono text-xs text-muted-foreground">{p.slug}</span>
              </TableCell>
              <TableCell className="text-right font-mono">v{p.version}</TableCell>
              <TableCell className="text-xs">
                <RelativeTime at={p.updated_at} />
              </TableCell>
              <TableCell className="max-w-56 truncate text-xs text-muted-foreground">{changedBy(p.updated_by)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Frame>
  );
}

function ChangeRow({ change, name }: { change: OpsPlanChange; name: string }) {
  const [all, setAll] = useState(false);
  const lines = diffPlan(change.before, change.after);
  const shown = all ? lines : lines.slice(0, DIFF_PREVIEW);
  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-sm font-medium">{name}</span>
        <Badge variant="muted" size="sm">
          v{change.version}
        </Badge>
        {change.before === null && (
          <Badge variant="info" size="sm">
            created
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">
          {changedBy(change.by)} · <RelativeTime at={change.at} />
        </span>
      </div>
      {change.reason && <p className="text-sm text-muted-foreground">{change.reason}</p>}
      {lines.length === 0 ? (
        <p className="text-xs text-muted-foreground">No field changed.</p>
      ) : (
        <ul className="flex flex-col gap-0.5 rounded-md bg-muted/50 px-3 py-2 font-mono text-xs">
          {shown.map((line) => (
            <DiffLine key={line.path} line={line} />
          ))}
          {lines.length > DIFF_PREVIEW && (
            <li>
              <button type="button" onClick={() => setAll((a) => !a)} className="cursor-pointer font-sans text-muted-foreground hover:text-foreground hover:underline">
                {all ? "Show less" : `Show all ${lines.length}`}
              </button>
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

function DiffLine({ line }: { line: PlanDiffLine }) {
  return (
    <li className="flex min-w-0 flex-wrap gap-x-2">
      <span className={cn("shrink-0", line.kind === "added" && "text-success", line.kind === "removed" && "text-destructive")}>
        {line.kind === "added" ? "+" : line.kind === "removed" ? "−" : "~"} {line.path}
      </span>
      <span className="min-w-0 break-all text-muted-foreground">
        {line.kind === "changed" && (
          <>
            <span className="line-through">{line.before}</span> → <span className="text-foreground">{line.after}</span>
          </>
        )}
        {line.kind === "added" && <span className="text-foreground">{line.after}</span>}
        {line.kind === "removed" && <span className="line-through">{line.before}</span>}
      </span>
    </li>
  );
}
